// Ordered product add-ons. The vendor submit handler in app.js drives the bridge:
// begin() -> null (native path), false (blocked) or true (owned preflight);
// submit(main, complete) posts once; finishPreflight(error) releases an owned preflight that never posted.
if (!customElements.get('almila-product-addons')) {
  const REQUEST_TIMEOUT = 20000;
  const MONEY_FORMATS = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_no_decimals_with_space_separator: [0, ' ', ','],
    amount_with_space_separator: [2, ' ', ','],
    amount_with_period_and_space_separator: [2, ' ', '.']
  };
  const cents = (value) => (value === null || value === '' ? NaN : Number(value));
  const plainText = (html) => new DOMParser().parseFromString(String(html ?? ''), 'text/html').documentElement.textContent;

  customElements.define('almila-product-addons', class extends HTMLElement {
    constructor() {
      super();
      this.phase = 'idle';
    }

    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.isConnected && this.connectedCallback(), { once: true });
        return;
      }
      this.disconnectedCallback();
      this.form = this.closest('form.add-to-cart-form');
      try {
        this.config = JSON.parse(this.querySelector('script[data-addons-config]').textContent);
      } catch {
        this.config = null;
      }
      this.ready = Boolean(this.form && Array.isArray(this.config?.variants));
      if (!this.ready) return;
      // Selections never start checked, even when the browser restores form state on reload.
      if (!this.started) for (const box of this.choices()) box.checked = false;
      this.started = true;
      this.moneyFormat = plainText(this.config.moneyFormat);
      this.abort = new AbortController();
      const { signal } = this.abort;
      for (const type of ['change', 'input', 'click']) this.form.addEventListener(type, () => this.render(), { signal });
      this.part('add-all')?.addEventListener('click', () => this.addAll(), { signal });
      // A cart navigation restored from the back-forward cache must not keep the form locked.
      window.addEventListener('pageshow', (event) => {
        if (event.persisted && this.phase === 'navigating') this.settle('idle', '');
      }, { signal });
      this.observer = new MutationObserver((records) => {
        if (records.some(record => record.target === this.nativeButton())) this.render();
      });
      this.observer.observe(this.form, { attributes: true, attributeFilter: ['disabled'], subtree: true });
      this.restoreDisclosure(signal);
      this.render();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
    }

    begin() {
      if (!this.ready) return null;
      const all = this.intent === 'all';
      this.intent = null;
      if (this.phase === 'locked') {
        this.say('inspectCart', true);
        return false;
      }
      if (this.phase !== 'idle') return false;
      const extras = this.selectedIds();
      if (!extras.length) return all ? false : null;
      this.phase = 'preflight';
      this.mode = all ? 'all' : 'cta';
      this.extras = extras;
      this.mainSnapshot = this.mainSignature();
      this.say('pending');
      this.render();
      return true;
    }

    mainSignature() {
      return JSON.stringify([...new FormData(this.form)].map(([key, value]) => [key,
        typeof value === 'string' ? value : { name: value.name, size: value.size, lastModified: value.lastModified }
      ]));
    }

    validatePreflight() {
      if (this.phase !== 'preflight') return false;
      if (this.mainSignature() === this.mainSnapshot) return true;
      this.settle('idle', 'selectionChanged');
      return false;
    }

    finishPreflight(error) {
      if (this.phase === 'preflight') this.settle('idle', error ? 'preflightError' : '');
    }

    submit(main, complete) {
      if (this.phase !== 'preflight') return Promise.resolve(false);
      const id = String(main?.id ?? '');
      const quantity = Number(main?.quantity);
      if (!/^\d+$/.test(id) || !Number.isInteger(quantity) || quantity < 1) {
        this.settle('idle', 'invalidSelection');
        return Promise.resolve(false);
      }
      this.phase = 'posting';
      const line = { id, quantity };
      if (main.properties && Object.keys(main.properties).length) line.properties = main.properties;
      if (main.selling_plan) line.selling_plan = main.selling_plan;
      const items = [line, ...this.extras.filter(extra => extra !== id).map(extra => ({ id: extra, quantity: 1 }))];
      return this.post(items, this.mode, complete);
    }

    async post(items, mode, complete) {
      let lines = null;
      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items }),
          signal: AbortSignal.timeout?.(REQUEST_TIMEOUT)
        });
        const data = await response.json();
        if (response.ok) lines = this.confirmed(items, data);
      } catch {
        // Network failure, timeout or unreadable body: the add may still have happened.
      }
      if (!lines) return this.uncertain();
      for (const box of this.choices()) box.checked = false;
      if (mode === 'all') {
        this.settle('navigating', 'success');
        window.location.assign(this.dataset.cartUrl);
        return true;
      }
      let completed = false;
      try {
        completed = (await complete(lines)) === true;
      } catch {
        completed = false;
      }
      this.settle('idle', completed ? 'success' : 'refreshError', !completed);
      return completed;
    }

    // Response quantities can include earlier cart quantities, so they only need to cover the request.
    confirmed(items, data) {
      if (!Array.isArray(data?.items)) return null;
      const lines = items.map(item => data.items.find(line => String(line?.id) === item.id));
      return lines.every((line, index) => Number(line?.quantity) >= items[index].quantity) ? lines : null;
    }

    // Any unconfirmed add may be partial (Shopify can add available stock and still answer 422),
    // so the form stays locked until reload and the cart is only read, never retried.
    async uncertain() {
      this.settle('locked', 'uncertain', true);
      try {
        await fetch(this.dataset.cartJsonUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: AbortSignal.timeout?.(REQUEST_TIMEOUT)
        });
      } catch {
        // The cart link remains the way to inspect the cart.
      }
      return false;
    }

    addAll() {
      const button = this.nativeButton();
      if (this.phase !== 'idle' || !button || button.disabled || !this.selectedIds().length) return;
      // Submit through the native form so Add All gets the same validation, preflight and main item.
      this.intent = 'all';
      try {
        this.form.requestSubmit(button);
      } finally {
        this.intent = null;
      }
    }

    render() {
      if (!this.ready) return;
      const idle = this.phase === 'idle';
      const boxes = this.choices();
      for (const box of boxes) if (box.disabled === idle) box.disabled = !idle;
      const selected = boxes.filter(box => box.checked);
      const button = this.nativeButton();
      const addAll = this.part('add-all');
      const blocked = !idle || !selected.length || !button || button.disabled;
      if (addAll && addAll.disabled !== blocked) addAll.disabled = blocked;
      const sums = selected.length ? this.totals(selected) : null;
      for (const name of ['totals', 'total']) this.show(name, Boolean(sums));
      for (const name of ['savings-row', 'savings']) this.show(name, Boolean(sums) && sums.savings > 0);
      if (!sums) return;
      this.write('total', this.money(sums.total));
      this.write('savings', this.money(sums.savings));
    }

    totals(selected) {
      const main = this.mainLine();
      if (!main) return null;
      const lines = [main, ...selected.map(box => ({
        price: cents(box.dataset.price),
        compareAtPrice: cents(box.dataset.compareAtPrice),
        quantity: 1
      }))];
      if (lines.some(line => !Number.isFinite(line.price))) return null;
      return {
        total: lines.reduce((sum, line) => sum + line.price * line.quantity, 0),
        savings: lines.reduce((sum, line) => sum + Math.max(0, (line.compareAtPrice || 0) - line.price) * line.quantity, 0)
      };
    }

    // Reads the same fields the vendor handler posts; an unknown selling plan hides the total rather than guess.
    mainLine() {
      const id = this.form.querySelector('#js-product-id')?.value;
      const quantity = Number(this.form.querySelector('input[name="quantity"]')?.value);
      const plan = this.form.querySelector('[name="selling_plan"]')?.value;
      const variant = this.config.variants.find(record => String(record.id) === id);
      const target = plan ? variant?.sellingPlans?.find(record => String(record.id) === plan) : variant;
      if (!target || !Number.isInteger(quantity) || quantity < 1) return null;
      return { price: cents(target.price), compareAtPrice: cents(target.compareAtPrice), quantity };
    }

    money(value) {
      const match = this.moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!match) {
        try {
          return new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency }).format(value / 100);
        } catch {
          return (value / 100).toFixed(2);
        }
      }
      const [decimals, thousands, separator] = MONEY_FORMATS[match[1]] || MONEY_FORMATS.amount;
      const [whole, fraction] = (value / 100).toFixed(decimals).split('.');
      return this.moneyFormat.replace(match[0], whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? separator + fraction : ''));
    }

    restoreDisclosure(signal) {
      const details = this.querySelector('details[data-addons-disclosure]');
      if (!details) return;
      const key = `almila-product-addons:${this.dataset.productId}`;
      // Storage can be unavailable (private mode); the disclosure then keeps its rendered state.
      try {
        const saved = localStorage.getItem(key);
        if (saved) details.open = saved === 'open';
      } catch {}
      details.addEventListener('toggle', () => {
        try {
          localStorage.setItem(key, details.open ? 'open' : 'closed');
        } catch {}
      }, { signal });
    }

    settle(phase, key, showCart = false) {
      this.phase = phase;
      this.say(key, showCart);
      this.render();
    }

    say(key, showCart = false) {
      const status = this.part('status');
      if (status) status.textContent = key ? this.config.messages?.[key] ?? '' : '';
      this.show('cart-link', showCart);
    }

    write(name, text) {
      const element = this.part(name);
      if (element) element.textContent = text;
    }

    show(name, visible) {
      const element = this.part(name);
      if (element) element.hidden = !visible;
    }

    part(name) {
      return this.querySelector(`[data-addons-${name}]`);
    }

    choices() {
      return [...this.querySelectorAll('input[type="checkbox"][data-addon-choice]')];
    }

    selectedIds() {
      return [...new Set(this.choices().filter(box => box.checked && /^\d+$/.test(box.value)).map(box => box.value))];
    }

    nativeButton() {
      return this.form?.querySelector('button[name="add"]') || null;
    }
  });
}
